import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators'
import _ from 'lodash'
import { DataTablePagination } from '@/types/_'
import Apis from '@/Apis/_'
import moment from 'moment'
import { flatPhone } from '@/utils/common'
import { Pais } from '@/components/Suporte/Store/SuportePaisesStore'

type Mode = 'CREATE' | 'EDIT'

type SetSuporteModal = {
  name: string,
  value: boolean,
}

type SuporteModal = {
  paciente: boolean,
}

interface PacienteHospital {
  hospitalNome: string,
  hisId?: string,
  hospitalId: string,
}

interface Paciente {
  id: string,
  nome: string,
  dataNascimento: string,
  nomeMae?: string,
  sexo?: string,
  email?: string,
  telefone?: string,
  celular?: string,
  cpf?: string,
  pacienteNaoPossuiCpf: boolean,
  cpfResponsavel?: string,
  certidaoNascimentoFolha?: string,
  certidaoNascimentoLivro?: string,
  nomeSocial?: string,
  rne?: string,
  certidaoNascimentoTermo?: string,
  rgh?: string,
  passaporte?: string,
  paisId?: string | null,
  tipoPaciente?: string | null,
  pacienteHospitais: PacienteHospital[],
}

interface PacienteResponse {
  paciente: Paciente,
  pacienteHospitals: PacienteHospital[],
}

export type ContextoDB = 'FinxAgendamentoContext' | 'FinxAgendamentoUhgContext' | 'FinxAgendamentoDasaContext'

@Module({ namespaced: true, name: 'SuportePacientesStore' })
export default class SuportePacientesStore extends VuexModule {
  public contextoDB: ContextoDB = 'FinxAgendamentoContext'

  public mode: Mode = 'CREATE'

  public modal: SuporteModal = {
    paciente: false,
  }

  @Mutation
  setContextoDB(value: ContextoDB) {
    this.contextoDB = value
  }

  @Mutation
  setModal(modal: SetSuporteModal) {
    this.modal[modal.name] = modal.value
  }

  @Mutation
  setMode(param: any) {
    this.mode = param.mode
  }

  @Action
  forceCast<T>(obj: any) {
    return obj
  }

  /** Pacientes */
  private paginationPacientesDefault: DataTablePagination = {
    descending: false,
    page: 1,
    rowsPerPage: 5,
    sortBy: null,
    totalItems: 0,
    rowsPerPageItems: [5, 10, 25],
  }

  public paginationPacientes: DataTablePagination = { ...this.paginationPacientesDefault }

  get pacientePaginationDefault() {
    return this.paginationPacientesDefault
  }

  private pacientesDefault: Paciente[] = []
  public pacientes: Paciente[] = [...this.pacientesDefault]

  public searchPaciente: string = ''

  @Mutation
  setSearchPaciente(value: string) {
    this.searchPaciente = value
  }

  @Mutation
  setPaginationPacientes(val: DataTablePagination) {
    this.paginationPacientes = val
  }

  @Mutation
  setPacientes(value: Paciente[]) {
    this.pacientes = value
  }

  @Mutation
  clearPacientes() {
    this.pacientes = [...this.pacientesDefault]
  }

  @Action
  async getPacientesPagedSearch(pagination: DataTablePagination) {
    try {
      const params: any = {
        contextoDB: this.contextoDB,
        pagina: pagination.page || this.paginationPacientesDefault.page,
        qtdPorPagina: pagination.rowsPerPage || this.paginationPacientesDefault.rowsPerPage,
        searchText: this.searchPaciente,
      }

      const request = await Apis.Agendamento.SuporteApi.getAllPaciente(params)

      if (request.success) {
        this.context.commit('setPacientes', request.data.paciente as Paciente[] || [])
        this.context.commit('setPaginationPacientes', {
          descending: false,
          page: pagination.page || this.paginationPacientesDefault.page,
          rowsPerPage: pagination.rowsPerPage || this.paginationPacientesDefault.rowsPerPage,
          sortBy: null,
          totalItems: request.data.total,
          rowsPerPageItems: this.paginationPacientesDefault.rowsPerPageItems,
        })
      }

      return request
    } catch (error) {
      this.context.commit('setPacientes', [])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  pacienteDefault: Paciente = {
    id: '',
    nome: '',
    dataNascimento: '',
    nomeSocial: '',
    nomeMae: '',
    sexo: '',
    email: '',
    telefone: '',
    celular: '',
    cpf: '',
    pacienteNaoPossuiCpf: false,
    cpfResponsavel: '',
    certidaoNascimentoFolha: '',
    certidaoNascimentoLivro: '',
    certidaoNascimentoTermo: '',
    rne: '',
    rgh: '',
    passaporte: '',
    paisId: null,
    tipoPaciente: null,
    pacienteHospitais: [],
  }

  paciente: Paciente = { ...this.pacienteDefault }

  @Mutation
  setPaciente(value: PacienteResponse) {
    this.paciente = { ...this.paciente, ...value.paciente }
    this.paciente.pacienteHospitais = value.pacienteHospitals
    if (this.paciente.dataNascimento) this.paciente.dataNascimento = moment(this.paciente.dataNascimento).format('YYYY-MM-DD')
  }

  @Mutation
  clearPaciente() {
    this.paciente = { ...this.pacienteDefault }
  }

  @Action
  async getPacienteById(id: string) {
    try {
      const request = await Apis.Agendamento.SuporteApi.getPacienteById(id)
      if (request.success && Array.isArray(request.data)) this.context.commit('setPaciente', request.data[0] as PacienteResponse)
      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  @Action
  async updatePaciente() {
    try {
      const params: any = { ...this.paciente }
      params.dataNascimento = params.dataNascimento ? `${params.dataNascimento}T00:00:00` : ''
      params.telefone = params.telefone ? flatPhone(params.telefone) : ''
      params.celular = params.celular ? flatPhone(params.celular) : ''
      params.pacienteHospitais = params.pacienteHospitais.map((e: any) => ({ pacienteId: params.id, ...e }))

      Object.entries(params).forEach(([key, value]) => {
        if (['', null, undefined].includes(value as any)) delete params[key]
      })

      const request = await Apis.Agendamento.SuporteApi.updatePaciente(params)
      return request
    } catch (error) {
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }

  /** PAÍSES */
  public paises: any[] = []

  @Mutation
  setPaises(value: any[]) {
    this.paises = value
  }

  @Action
  async getPaisAll() {
    try {
      const request = await Apis.Agendamento.SuporteApi.getPaisAll('ae98fe42-170b-44e4-e898-08d7d7fcde05') // HOSPITAL VERA CRUZ
      if (request.success) this.context.commit('setPaises', request.data as Pais[] || [])
      return request
    } catch (error) {
      this.context.commit('setPaises', [])
      return { success: false, message: 'Ocorreu um erro na requisição.' }
    }
  }
}
